import { lazy } from 'react';
// CUSTOM COMPONENTS
import Loadable from './Loadable';
import { AuthGuard } from 'components/auth';
import LayoutV1 from 'layouts/layout-1';

const BillingItems = Loadable(
  lazy(() => import('pages/admin/billingItems/billingItems'))
);
const Payors = Loadable(lazy(() => import('pages/admin/payors/payors')));
const AddEditPayor = Loadable(
  lazy(() => import('pages/admin/payors/components/addEditPayor'))
);
const PayorItems = Loadable(
  lazy(() => import('pages/admin/payors/payorItems/payorItems'))
);

const TripForms = Loadable(
  lazy(() => import('pages/admin/tripForms/tripForms'))
);
const AddEditTripForm = Loadable(
  lazy(() => import('pages/admin/tripForms/components/addEditTripForm'))
);
const SingleDynamicForm = Loadable(
  lazy(() => import('pages/admin/singleDynamicForm/singleDynamicForm'))
);
const Facilities = Loadable(
  lazy(() => import('pages/admin/facilities/facilities'))
);
const AddEditFacility = Loadable(
  lazy(() => import('pages/admin/facilities/components/addEditFacility'))
);
const Providers = Loadable(
  lazy(() => import('pages/admin/providers/providers'))
);
const AddEditProvider = Loadable(
  lazy(() => import('pages/admin/providers/components/addEditProvider'))
);
const AcceptDetails = Loadable(
  lazy(() => import('pages/admin/connections/components/acceptDetails'))
);

const AddEditZone = Loadable(
  lazy(() => import('pages/admin/zones/components/addEditZone'))
);
const ZonesList = Loadable(lazy(() => import('pages/admin/zones/zones')));

const ConnectionsList = Loadable(
  lazy(() => import('pages/admin/connections/connections'))
);

const ActivityLogs = Loadable(
  lazy(() => import('pages/admin/activityLogs/activityLogs'))
);

const CompanyList = Loadable(
  lazy(() => import('pages/admin/company/companyList'))
);

const CompanyInfo = Loadable(
  lazy(() => import('pages/admin/company/components/editCompanyInfo'))
);

const AddEditCompany = Loadable(
  lazy(() => import('pages/admin/company/components/addEditCompany'))
);

const PrivatePayForms = Loadable(
  lazy(() => import('pages/admin/tripForms/privatePayForms'))
);

const AddEditPrivatePayForm = Loadable(
  lazy(
    () => import('pages/admin/tripForms/components/addEditPrivatePayTripForm')
  )
);
const Invoices = Loadable(lazy(() => import('pages/admin/invoices/invoices')));
const InvoiceDetails = Loadable(
  lazy(() => import('pages/admin/invoices/invoicesList/invoiceDetails'))
);

const AddEditPaymentMethod = Loadable(
  lazy(() => import('pages/admin/paymentsList/addEditPaymentMethod'))
);

const AddNotificationEmail = Loadable(
  lazy(
    () => import('pages/admin/invoices/notificationsList/addNotificationEmail')
  )
);
const PaymentList = Loadable(
  lazy(() => import('pages/admin/paymentsList/paymentsList'))
);

const OpenApi = Loadable(lazy(() => import('pages/admin/openApi/openApi')));

const RolesAndPermissions = Loadable(
  lazy(() => import('pages/admin/rolesAndPermissions/rolesAndPermissions'))
);
const AddNewRole = Loadable(
  lazy(
    () => import('pages/admin/rolesAndPermissions/components/addNewRole')
  )
);

const Users = Loadable(lazy(() => import('pages/admin/users/users')));
const Customers = Loadable(lazy(() => import('pages/admin/customers/customers')));
const AddUser = Loadable(
  lazy(() => import('pages/admin/users/add-new-user'))
);
const Groups = Loadable(lazy(() => import('pages/admin/groups/groups')));
const AddEditGroup = Loadable(
  lazy(() => import('pages/admin/groups/components/addEditGroup'))
);

const VehicleTypes = Loadable(
  lazy(() => import('pages/admin/vehicleTypes/vehicleTypes'))
);
const AddEditVehicleType = Loadable(
  lazy(
    () => import('pages/admin/vehicleTypes/components/addEditVehicleType')
  )
);

const Vehicles = Loadable(
  lazy(() => import('pages/admin/vehicles/vehicles'))
);
const AddEditVehicle = Loadable(
  lazy(() => import('pages/admin/vehicles/components/addEditVehicle'))
);
const Drivers = Loadable(lazy(() => import('pages/admin/drivers/drivers')));
const AddEditDriver = Loadable(
  lazy(() => import('pages/admin/drivers/components/addEditDriver'))
);

export const AdminRoutes = [
  {
    path: 'admin',
    element: (
      <AuthGuard>
        <LayoutV1 />
      </AuthGuard>
    ),
    children: [
      {
        path: 'add-facility-credit-card/:id',
        element: <PaymentList />
      },
      {
        path: 'user-info',
        element: <PaymentList />
      },
      {
        path: 'roles-and-permissions',
        element: <RolesAndPermissions />
      },
      {
        path: 'add-new-role',
        element: <AddNewRole />
      },
      {
        path: 'user-list/:listType',
        element: <Users />
      },
      {
        path: 'customers/:listType',
        element: <Customers />
      },
      {
        path: 'add-user',
        element: <AddUser />
      },
      {
        path: 'edit-user/:id',
        element: <AddUser />
      },
      {
        path: 'groups',
        element: <Groups />
      },
      {
        path: 'add-new-group',
        element: <AddEditGroup />
      },
      {
        path: 'edit-group/:id',
        element: <AddEditGroup />
      },
      {
        path: 'vehicle-types',
        element: <VehicleTypes />
      },
      {
        path: 'add-new-vehicle-type',
        element: <AddEditVehicleType />
      },
      {
        path: 'edit-vehicle-type/:id',
        element: <AddEditVehicleType />
      },
      {
        path: 'vehicles',
        element: <Vehicles />
      },
      {
        path: 'add-new-vehicle',
        element: <AddEditVehicle />
      },
      {
        path: 'edit-vehicle/:id',
        element: <AddEditVehicle />
      },
      {
        path: 'drivers',
        element: <Drivers />
      },
      {
        path: 'add-new-driver',
        element: <AddEditDriver />
      },
      {
        path: 'edit-driver/:id',
        element: <AddEditDriver />
      },
      {
        path: 'additional-services',
        element: <BillingItems />
      },
      {
        path: 'payors',
        element: <Payors />
      },
      {
        path: 'add-new-payor',
        element: <AddEditPayor />
      },
      {
        path: 'edit-payor/:id',
        element: <AddEditPayor />
      },
      {
        path: 'payor-items/:id',
        element: <PayorItems />
      },
      {
        path: 'trip-forms',
        element: <TripForms />
      },
      {
        path: 'add-edit-trip-form',
        element: <AddEditTripForm />
      },
      {
        path: 'add-edit-trip-form/:id',
        element: <AddEditTripForm />
      },
      {
        path: 'dynamic-form/:formType',
        element: <SingleDynamicForm />
      },
      {
        path: 'facilities',
        element: <Facilities />
      },
      {
        path: 'add-facility',
        element: <AddEditFacility />
      },
      {
        path: 'edit-facility/:id',
        element: <AddEditFacility />
      },
      {
        path: 'add-provider',
        element: <AddEditProvider />
      },
      {
        path: 'edit-provider/:id',
        element: <AddEditProvider />
      },
      {
        path: 'providers',
        element: <Providers />
      },
      {
        path: 'connections',
        element: <ConnectionsList />
      },
      {
        path: 'accept-connection',
        element: <AcceptDetails />
      },
      {
        path: 'zones',
        element: <ZonesList />
      },
      {
        path: 'add-new-zone',
        element: <AddEditZone />
      },
      {
        path: 'edit-zone/:id',
        element: <AddEditZone />
      },
      {
        path: 'activity-logs',
        element: <ActivityLogs />
      },
      {
        path: 'company-list',
        element: <CompanyList />
      },
      {
        path: 'company-info/:id',
        element: <CompanyInfo />
      },
      {
        path: 'add-company',
        element: <AddEditCompany />
      },
      {
        path: 'edit-company/:id',
        element: <AddEditCompany />
      },
      {
        path: 'private-pay-form/:id',
        element: <AddEditPrivatePayForm />
      },
      {
        path: 'private-pay-form',
        element: <AddEditPrivatePayForm />
      },
      {
        path: 'private-pay-forms',
        element: <PrivatePayForms />
      },
      {
        path: 'invoices',
        element: <Invoices />
      },
      {
        path: 'invoice-details/:id',
        element: <InvoiceDetails />
      },
      {
        path: 'edit-payment-method/:id',
        element: <AddEditPaymentMethod />
      },
      {
        path: 'add-new-payment-method',
        element: <AddEditPaymentMethod />
      },
      {
        path: 'add-new-email-notification',
        element: <AddNotificationEmail />
      },
      {
        path: 'open-api',
        element: <OpenApi />
      }
    ]
  }
];
